import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Store } from '../../../store';
import { myEventsCutoff } from '../my-events-cutoff';
import { useEventsStore } from '../store';
import type { ScheduledEvent } from './type';
import type { UseEventsHook } from './use-events';
import { useEvents } from './use-events';
import { useMyEvents } from './use-my-events';

const sameEmail = (a?: string, b?: string): boolean =>
  Boolean(a) && Boolean(b) && a?.trim().toLowerCase() === b?.trim().toLowerCase();

/*
 * "My Events" merged with the user's favorites. There's no single backend query
 * for both, so we run the my-events feed and (only when viewing your OWN events)
 * the favorites feed, and merge them client-side, de-duping by eid.
 *
 * Favorites apply ONLY when the selected validator is the logged-in user. For any
 * other validator we show just that validator's events — no favorites query fires
 * (useEvents skips when the id list is empty). Pagination/hasMore follow my-events.
 */
export const useMyEventsWithFavorites: UseEventsHook = (filters) => {
  const { me } = Store.User.useIdentity();
  const favoriteEvents = useEventsStore((v) => v.favoriteEvents);

  const mine = useMyEvents(filters);

  // Favorites only when the validator is the logged-in user (default = self).
  const viewingSelf = filters.verifierEmail === undefined || sameEmail(filters.verifierEmail, me?.email);
  const favoriteIds = viewingSelf ? [...favoriteEvents] : [];

  /*
   * Empty ids: useEvents skips the query, so no favorites search for other
   * validators.
   *
   * The limit must cover EVERY favorite rather than reuse the list's page size:
   * favorites are not paginated here, they are merged into the validator's events
   * in one pass. With more favorites than `filters.limit` the server returns only
   * the first page, sorted oldest-first, so the ones still ahead of us fall off the
   * end, the merge below drops the rest as already ended, and the tab renders
   * "No Events Found" even though the stats (which fetch every favorite) show a count.
   */
  const favorites = useEvents(
    { ...filters, eventIds: favoriteIds, limit: Math.max(favoriteIds.length, 1) },
    {}
  );

  const events = useMemo(() => {
    const mineEvents = mine.events;
    const favEvents = viewingSelf ? favorites.events : undefined;

    // Keep "loading with no data yet" distinct from "loaded, empty".
    if (mineEvents === undefined && favEvents === undefined) {
      return undefined;
    }

    const now = DateTime.now();
    const cutoff = myEventsCutoff();
    const seen = new Set<string>();
    const merged: ScheduledEvent[] = [];

    for (const event of mineEvents ?? []) {
      if (!seen.has(event.eid)) {
        seen.add(event.eid);
        merged.push(event);
      }
    }

    for (const event of favEvents ?? []) {
      /*
       * Only keep events still in the favorites set (a stale/cached query result
       * can otherwise linger after unfavoriting), and skip dupes.
       */
      if (seen.has(event.eid) || !favoriteEvents.has(event.eid)) {
        continue;
      }

      // My-events already drops ended events; do the same for favorites.
      const end = DateTime.fromISO(String(event.endTime ?? ''));

      if (!event.isLive && end.isValid && end < now) {
        continue;
      }

      // Cap favorites at the same 4 AM window the validator feed uses server-side.
      const start = DateTime.fromISO(String(event.startTime ?? ''));

      if (start.isValid && start > cutoff) {
        continue;
      }

      seen.add(event.eid);
      merged.push(event);
    }

    // Interleave favorites into the my-events order (by startTime, per its sortOrder).
    const direction = filters.sortOrder === 'DESC' ? -1 : 1;

    return merged.sort(
      (a, b) => direction * String(a.startTime ?? '').localeCompare(String(b.startTime ?? ''))
    );
  }, [mine.events, favorites.events, favoriteEvents, viewingSelf, filters.sortOrder]);

  return {
    ...mine,
    events,
    loading: mine.loading || (viewingSelf && favorites.loading),
    error: mine.error ?? favorites.error
  };
};
